import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetLeanConsolidatedDataQuery } from '@/redux/slices/preship/preship-api-slice';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { formatSummaryNumber } from '@/lib/formatSummaryNumber';

interface ConsolidatedViewProps {
  isOpen?: boolean;
  onClose?: () => void;
  variant: string;
  activeWeek?: string
}

const LeanConsolidatedView: React.FC<ConsolidatedViewProps> = ({ isOpen, onClose, variant, activeWeek }) => {
  const [activeTab, setActiveTab] = useState('raw');
  const [activeStatistic, setActiveStatistic] = useState('max');
  const { projectId } = useSelector((state: RootState) => state.define);
  const { target } = useSelector((state: RootState) => state.define);
  const { data: consolidatedData, isLoading, error, refetch } = useGetLeanConsolidatedDataQuery({ projectId, variant, weekType: activeWeek }, { refetchOnMountOrArgChange: true });
  useEffect(() => {
    if (isOpen === true) {
      refetch()
    }
  }, [isOpen])
  console.log(projectId, consolidatedData, 'consolidatedData', error)

  // Define getLeanCellColor function at component level for reuse
  const getLeanCellColor = (leanValue: string) => {
    const numericLean = parseFloat(leanValue);
    const targetValue = Number(target) || 2; // Use project target or fallback to 2
    if (!isNaN(numericLean)) {
      if (numericLean >= -targetValue && numericLean <= targetValue) {
        return "bg-states-success text-white";
      } else {
        return "bg-bgRed text-white";
      }
    }
    return "";
  };

  if (isLoading) return (
    <div className="space-y-6 p-4">
      {/* Tab button skeletons */}
      <div className="flex gap-2 mb-4">
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-24" />
      </div>

      {/* Table skeleton */}
      <div className="border rounded-md overflow-hidden">
        <div className="border-b bg-gray-50">
          <div className="grid grid-cols-7 p-3">
            {Array(7).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full mx-2" />
            ))}
          </div>
        </div>
        <div className="p-2">
          {Array(5).fill(0).map((_, i) => (
            <div key={i} className="grid grid-cols-7 gap-2 p-2 border-b last:border-0">
              {Array(7).fill(0).map((_, j) => (
                <Skeleton key={j} className="h-10 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Graph skeleton */}
      <div className="mt-6">
        <div className="flex space-x-2 mb-4">
          {Array(4).fill(0).map((_, i) => (
            <Skeleton key={i} className="h-10 w-24" />
          ))}
        </div>
        <Skeleton className="h-[300px] w-full rounded-md" />
      </div>
    </div>
  );
  if (error) return <div></div>;

  const renderRawDataTable = () => {
    // 1. Group raw data by palletId
    const groupedByPalletId = consolidatedData.rawData.reduce((groups: Record<string, any[]>, row: any) => {
      const palletId = row.palletId || 'unknown';
      if (!groups[palletId]) {
        groups[palletId] = [];
      }
      groups[palletId].push(row);
      return groups;
    }, {});

    // 2. Sort groups by palletCreationDate and then sort each group by lpnIndex
    const sortedGroups = Object.entries(groupedByPalletId)
      // Sort groups by palletCreationDate
      .sort(([, groupA], [, groupB]) => {
        const dateA = new Date(groupA[0]?.palletCreationDate || 0).getTime();
        const dateB = new Date(groupB[0]?.palletCreationDate || 0).getTime();
        return dateA - dateB;
      })
      .map(([, group]) => {
        // Sort each group by lpnIndex
        return group.sort((a, b) => {
          const indexA = parseInt(a.lpnIndex) || 0;
          const indexB = parseInt(b.lpnIndex) || 0;
          return indexA - indexB;
        });
      });

    // 3. Flatten the sorted groups back into a single array
    const sortedData = sortedGroups.flat();

    return (
      <Table className="border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Pallet No</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Pallet Name</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Pallet Type</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side A (in)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side B (in)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side C (in)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side D (in)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.map((row: any, index: number) => (
            <TableRow key={`${row.palletId}-${row.lpnIndex || index}`} className={"bg-gray-100"}>
              <TableCell className="border text-center border-gray-300 text-center p-2">{row.lpnIndex || index + 1}</TableCell>
              <TableCell className="border text-center border-gray-300 text-center p-2">{row.palletName}</TableCell>
              <TableCell className="border text-center border-gray-300 text-center p-2">{row.palletType}</TableCell>
              <TableCell className={cn("border text-center border-gray-300 text-center p-2", getLeanCellColor(row.sideA))}>{row.sideA}</TableCell>
              <TableCell className={cn("border text-center border-gray-300 text-center p-2", getLeanCellColor(row.sideB))}>{row.sideB}</TableCell>
              <TableCell className={cn("border text-center border-gray-300 text-center p-2", getLeanCellColor(row.sideC))}>{row.sideC}</TableCell>
              <TableCell className={cn("border text-center border-gray-300 text-center p-2", getLeanCellColor(row.sideD))}>{row.sideD}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderStatisticsTable = () => {
    return (
      <div className="space-y-6">
        <Table className="border-collapse w-full">
          <TableHeader>
            <TableRow className='bg-gray-100'>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Statistic</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Pallet Type</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side A (in)</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side B (in)</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side C (in)</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side D (in)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {['Max', 'Min', 'Average', 'stdev', 'Count'].map((stat, index) => (
              <React.Fragment key={stat}>
                {consolidatedData.statistics[stat.toLowerCase()].map((row: any, rowIndex: any) => (
                  <TableRow key={`${stat}-${rowIndex}`} className={"bg-gray-100"}>
                    {rowIndex === 0 && (
                      <TableCell rowSpan={consolidatedData.statistics[stat.toLowerCase()].length} className="border border-gray-300 text-center p-2 font-semibold">
                        {stat}
                      </TableCell>
                    )}
                    <TableCell className="border border-gray-300 text-center p-2">{row.palletType}</TableCell>
                    <TableCell className={cn("border border-gray-300 text-center p-2", stat !== 'Count' ? getLeanCellColor(row.sideA) : "")}>{stat === 'Count' ? row.sideA : formatSummaryNumber(row.sideA)}</TableCell>
                    <TableCell className={cn("border border-gray-300 text-center p-2", stat !== 'Count' ? getLeanCellColor(row.sideB) : "")}>{stat === 'Count' ? row.sideB : formatSummaryNumber(row.sideB)}</TableCell>
                    <TableCell className={cn("border border-gray-300 text-center p-2", stat !== 'Count' ? getLeanCellColor(row.sideC) : "")}>{stat === 'Count' ? row.sideC : formatSummaryNumber(row.sideC)}</TableCell>
                    <TableCell className={cn("border border-gray-300 text-center p-2", stat !== 'Count' ? getLeanCellColor(row.sideD) : "")}>{stat === 'Count' ? row.sideD : formatSummaryNumber(row.sideD)}</TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>

        <Table className="border-collapse w-full">
          <TableHeader>
            <TableRow>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Row Labels</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Data Fill Status</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Overall pass/fail status</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side A Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side A Pass Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side B Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side B Pass Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side C Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side C Pass Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side D Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Side D Pass Count</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {consolidatedData.additionalStats.map((row: any, index: any) => (
              <TableRow key={index} className={"bg-gray-100"}>
                <TableCell className="border border-gray-300 text-center p-2">{row.rowLabel}</TableCell>
                <TableCell className="border border-gray-300 text-center p-2">{row.dataFillStatus}</TableCell>
                <TableCell className={`${row.overallStatus === 'Fail' ? "text-white bg-bgRed" : (row.overallStatus === 'Pass' && row.dataFillStatus !== 'Incomplete') ? " text-white bg-states-success" : " bg-[#F79E19] text-white"} border border-gray-300 text-center p-2`}>{(row.overallStatus === 'Pass' && row.dataFillStatus !== 'Incomplete') ? row.overallStatus : row.overallStatus === 'Fail' ? row.overallStatus : "Data missing"}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideAFailCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.sideAFailCount}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideAPassCount) > 0 ? "bg-states-success" : "bg-bgRed")}>{row.sideAPassCount}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideBFailCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.sideBFailCount}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideBPassCount) > 0 ? "bg-states-success" : "bg-bgRed")}>{row.sideBPassCount}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideCFailCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.sideCFailCount}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideCPassCount) > 0 ? "bg-states-success" : "bg-bgRed")}>{row.sideCPassCount}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideDFailCount) > 0 ? "bg-bgRed" : "bg-states-success")}>{row.sideDFailCount}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2 text-white", parseInt(row.sideDPassCount) > 0 ? "bg-states-success" : "bg-bgRed")}>{row.sideDPassCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderGraph = () => {
    const graphData = consolidatedData.statistics[activeStatistic].map((item: any) => ({
      name: item.palletType,
      SideA: parseFloat(item.sideA),
      SideB: parseFloat(item.sideB),
      SideC: parseFloat(item.sideC),
      SideD: parseFloat(item.sideD),
    }));

    return (
      <div className="space-y-4">
        <div className="flex space-x-2">
          {['max', 'min', 'average', 'stdev'].map((stat) => (
            <Button
              key={stat}
              onClick={() => setActiveStatistic(stat)}
              className={cn(
                "px-4 justify-start  hover:bg-blue hover:text-white",
                activeStatistic === stat ? " bg-blue text-white" : "bg-[#FAFAFA] text-gray-700"
              )}
            >
              {stat.charAt(0).toUpperCase() + stat.slice(1)}
            </Button>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar barSize={20} dataKey="SideA" fill="#004899" />
            <Bar barSize={20} dataKey="SideB" fill="#8884d8" />
            <Bar barSize={20} dataKey="SideC" fill="#82ca9d" />
            <Bar barSize={20} dataKey="SideD" fill="#ffc658" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>Lean Consolidated View {`(${variant === 'postShip' ? "Post Ship" : variant === 'preShip' ? "Pre Ship" : "Post Stack"})`} </DialogTitle>
        </DialogHeader>
        <div className="flex h-[calc(100vh-200px)]">
          <div className="w-48  pt-10 bg-bgBlue">
            <div className="">
              <Button
                className={cn(
                  "w-full justify-start rounded-none hover:bg-blue hover:text-white",
                  activeTab === 'raw' ? " bg-blue text-white" : " text-gray-700 bg-bgBlue"
                )}
                onClick={() => setActiveTab('raw')}
              >
                Raw Data
              </Button>
              <Button
                className={cn(
                  "w-full justify-start rounded-none hover:bg-blue hover:text-white",
                  activeTab === 'statistics' ? " bg-blue text-white" : "bg-bgBlue text-gray-700"
                )}
                onClick={() => setActiveTab('statistics')}
              >
                Statistics
              </Button>
              <Button
                className={cn(
                  "w-full justify-start rounded-none hover:bg-blue hover:text-white",
                  activeTab === 'graphs' ? " bg-blue text-white" : "bg-bgBlue text-gray-700"
                )}
                onClick={() => setActiveTab('graphs')}
              >
                Graphs
              </Button>
            </div>
          </div>
          <div className="flex-1 p-6 overflow-auto">
            {activeTab === 'raw' && renderRawDataTable()}
            {activeTab === 'statistics' && renderStatisticsTable()}
            {activeTab === 'graphs' && renderGraph()}
          </div>
        </div>
        {/* <div className="p-6 pt-0">
          <Button onClick={onClose}>Close</Button>
        </div> */}
      </DialogContent>
    </Dialog>
  );
};

export default LeanConsolidatedView;
